<template>
  <b-modal
    v-model="showModal"
    size="lg"
    id="stockLocations-modal"
    :title="edited.id > 0 ? 'Edit stock location details' : 'Add new stock location'"
    :ok-title="isFormLoading ? 'Saving...' : 'Save'"
    :ok-disabled="isFormLoading"
    cancel-variant="outline-secondary"
    @ok="onSubmit($event)"
  >
    <div
      v-if="isContentLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>
    <b-form v-else>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Name"
            label-for="stock-location-name"
          >
            <b-form-input
              id="stock-location-name"
              v-model="edited.name"
              placeholder="Name"
            />
            <small
              v-if="v$.edited?.name.$error"
              class="text-danger"
            >Name is required</small>
          </b-form-group>
          <b-form-group
            label="Description"
            label-for="stock-location-description"
          >
            <b-form-textarea
              id="stock-location-description"
              v-model="edited.description"
              placeholder="Description"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
          <b-form-group
            label="Parent location"
            label-for="parentLocationId"
          >
            <b-form-select
              id="parentLocationId"
              v-model="edited.parent_location_id"
            >
              <b-form-select-option :value="null">
                None
              </b-form-select-option>
              <template
                v-for="loc in stockLocations"
                :key="loc.id"
              >
                <b-form-select-option
                  v-if="loc.id !== edited.id"
                  :value="loc.id"
                >
                  {{ loc.name }}
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>

          <b-form-group
            label="Picture"
            label-for="stock-location-picture"
          >
            <b-form-file
              id="stock-location-picture"
              v-model="edited.picture"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
            />
            <img
              v-if="edited.picture_preview ?? false"
              :src="edited.picture_preview.includes('https') ? edited.picture_preview : '//' + edited.picture_preview"
              :style="{ width: '150px', border: '1px solid #DDDDDD' }"
              class="my-1"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script setup>
import { watch, ref, computed, reactive } from 'vue'
import { useStore } from 'vuex'
import {
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import useVuelidate from '@vuelidate/core'
import { minLength, required } from '@vuelidate/validators'
import { toastSuccess, toastFailure } from '@/libs/toastification'

const cloneObject = (object) => JSON.parse(JSON.stringify(object))

const defaultStockLocation = {
  id: null,
  name: null,
  description: null,
  picture: null,
  picture_preview: null,
  parent_location_id: null,
}

const props = defineProps({
  stockLocations: {
    type: Array,
    default: () => [],
  },
  stockLocation: {
    type: Object,
    default: null,
  },
  clickDate: {
    type: Date,
    required: true,
  },
})

const showModal = ref(false)
const isContentLoading = ref(true)
const isFormLoading = ref(false)

const store = useStore()

const stockLocationFromStore = computed(() => store.state.stockLocations.stockLocation)

const edited = reactive(cloneObject(defaultStockLocation))

const rules = {
  edited: {
    name: { required, minLength: minLength(2) },
  },
}
const v$ = useVuelidate(rules, { edited })

watch(stockLocationFromStore, (newVal) => {
  if (newVal?.data && newVal.data.id > 0) {
    edited.id = newVal.data.id
    edited.name = newVal.data.name
    edited.description = newVal.data.description
    edited.picture_preview = newVal.data.picture
    edited.parent_location_id = newVal.data.parent_location_id
  } else {
    Object.assign(edited, cloneObject(defaultStockLocation))
  }
})

watch(
  () => props.clickDate,
  () => {
    if (props.stockLocation !== null) {
      const stockLocationId = props.stockLocation && props.stockLocation.id
      store.dispatch('stockLocations/loadStockLocation', stockLocationId)
        .then(() => {
          isContentLoading.value = false
          showModal.value = true
        })
        .catch((error) => {
          isContentLoading.value = false
          showModal.value = false
          toastFailure('Something went wrong: ' + error.message)
        })
    } else {
      Object.assign(edited, cloneObject(defaultStockLocation))
      isContentLoading.value = false
      showModal.value = true
    }
  },
)

const onSubmit = async (event) => {
  if (isFormLoading.value) {
    return
  }

  event.preventDefault()
  isFormLoading.value = true
  const isFormCorrect = await v$.value.edited.$validate()
  if (!isFormCorrect) {
    isFormLoading.value = false
    return
  }

  await submitForm()
}

const getTemporaryPicture = async () => {
  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.addEventListener('load', () => {
      resolve()
    }, false)

    if (edited.picture && /\.(jpe?g|png|gif)$/i.test(edited.picture.name)) {
      reader.readAsDataURL(edited.picture)
    } else {
      resolve()
    }
  })
}

const submitForm = async () => {
  const formData = new FormData()

  const appendIfPresent = (key, value) => {
    if (value !== null && value !== undefined && value !== '') {
      formData.append(key, value)
    }
  }

  if (edited.id != null) {
    appendIfPresent('id', edited.id)
  }
  appendIfPresent('name', edited.name)
  appendIfPresent('description', edited.description)
  appendIfPresent('parent_location_id', edited.parent_location_id)
  if (edited.picture) {
    formData.append('picture', edited.picture)
  }

  if (edited.picture) {
    await getTemporaryPicture()
  }

  if (edited.id != null) {
    formData.append('_method', 'PUT')

    store.dispatch('stockLocations/updateStockLocation', { formData })
      .then(() => {
        showModal.value = false
        toastSuccess('Successfully updated')
      })
      .catch(() => {
        toastFailure('Failed to update')
      })
      .finally(() => {
        isFormLoading.value = false
      })
  } else {
    formData.append('_method', 'POST')
    store.dispatch('stockLocations/addStockLocation', { formData })
      .then(() => {
        showModal.value = false
        toastSuccess('Successfully added')
      })
      .catch(() => {
        toastFailure('Failed to add')
      })
      .finally(() => {
        isFormLoading.value = false
      })
  }

  edited.picture = null
}

</script>
